import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import Langs, { Localize } from "../../../app/lang/langs";
import NetworkManager from "../../../app/managers/networkManager";
import { formatFileSize } from "./NewsAttachmentsPicker";

/**
 * The files hanging off a news post, as stored: the first image is shown as the post's
 * illustration, everything else is listed as a document.
 *
 * Which row is "the image" is not recorded in the schema — it does not need to be. The
 * picker uploads the illustration first and the backend returns the rows in id order, so
 * the first image mime is the one that was chosen as the picture.
 */
export default function NewsAttachmentsView({ attachments }) {

    if (!attachments || !attachments.length) return null;

    const network = NetworkManager.getInstance();
    const imageIndex = attachments.findIndex(a => a.mime && a.mime.startsWith("image/"));
    const image = imageIndex >= 0 ? attachments[imageIndex] : null;
    const files = attachments.filter((_, index) => index !== imageIndex);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {image && (
                // Opens full size in a new tab: the backend streams it `inline`, so the
                // browser shows the picture rather than downloading it.
                <Link
                    href={network.getNewsAttachmentUrl(image.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: "block", lineHeight: 0 }}
                >
                    <Box
                        component="img"
                        src={network.getNewsAttachmentUrl(image.id)}
                        alt={image.original_name || Localize("NEWS_IMAGE")}
                        loading="lazy"
                        sx={{
                            width: "100%",
                            maxHeight: 320,
                            objectFit: "contain",
                            borderRadius: "12px",
                            border: "1px solid rgba(0, 40, 128, 0.10)",
                            backgroundColor: "rgba(0, 0, 0, 0.02)",
                        }}
                    />
                </Link>
            )}

            {files.length > 0 && (
                <Box>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#6b6560", mb: 0.5 }}>
                        <Langs str="ATTACHMENTS" />
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {files.map((file) => (
                            <Box
                                key={file.id}
                                sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}
                            >
                                {file.mime === "application/pdf"
                                    ? <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: "#d32f2f" }} />
                                    : <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: "#002880" }} />}
                                <Link
                                    href={network.getNewsAttachmentUrl(file.id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={file.original_name}
                                    sx={{
                                        fontSize: "0.85rem",
                                        color: "#002880",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {file.original_name}
                                </Link>
                                <Typography sx={{ fontSize: "0.75rem", color: "#9a938c", whiteSpace: "nowrap" }}>
                                    {formatFileSize(file.size)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
